import { faqNodeId, pageNodeId } from '@/src/lib/seo/json-ld';
import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type FaqAnswerType = 'answer' | 'unorderedList' | 'orderedList';

export type FaqQuestion = {
  question: string;
  answer: string | Array<string>;
  answerType?: FaqAnswerType;
  listHeader?: string;
  /**
   * What to publish as the schema answer, when it must differ from the visible
   * one. The accordion may deflect to the property manager; a language model
   * quotes whatever it is given, so the published answer carries only the part
   * backed by data.
   */
  schemaAnswer?: string | Array<string>;
};

const NUMBER_WORDS: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
};

const numberAsWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

const asCurrency = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

type CondensedAmenitiesShape = {
  pets?: string | null;
  laundry?: string | null;
  parking?: boolean | null;
};

type ModelPricingShape = {
  numBeds?: number;
  quantity?: number;
  lowPrice?: number;
};

type DistrictShape = {
  fullName?: string;
};

/**
 * Builds the FAQ question set for an active HDP listing.
 *
 * Returns an empty array when the listing has too little data to populate
 * the schema-template defaults — callers should treat that as "no FAQ".
 */
export const getHdpFaqQuestions = (listing: PropertyDetailListing | null | undefined): Array<FaqQuestion> => {
  if (!listing || listing.active === false) return [];

  const isHouse = listing.propertyType === 'house';
  const buildingName = listing.displayName;

  const condensed = (listing.amenities as unknown as { condensedAmenities?: CondensedAmenitiesShape } | undefined)
    ?.condensedAmenities;
  const petPolicy = condensed?.pets ?? null;
  const formattedPet = petPolicy ? petPolicy.toLowerCase() : null;
  const laundry = condensed?.laundry ?? null;
  const formattedLaundry = laundry ? laundry.toLowerCase() : null;
  const parking = condensed?.parking ?? null;

  const city = listing.address?.city;
  const neighborhood = (listing.address as { neighborhood?: string } | undefined)?.neighborhood;
  const districts = (listing as { areas?: { districts?: Array<DistrictShape> } }).areas?.districts;
  const schoolDistrict = districts && districts.length > 0 ? districts[0].fullName : null;
  const lastUpdatedAgo = (listing as { history?: { lastUpdated?: { ago?: string } } }).history?.lastUpdated?.ago;
  const modelsAndPricing =
    listing.faqModelsAndPricing ?? (listing as { modelsAndPricing?: Array<ModelPricingShape> }).modelsAndPricing;

  const formattedUnits =
    modelsAndPricing
      ?.filter((unit) => (unit.quantity ?? 0) > 0)
      .map((unit) => {
        const beds = unit.numBeds ?? 0;
        const bedString = beds === 0 ? 'studio apartment' : `${numberAsWord(beds)}-bedroom apartment`;
        const qty = unit.quantity ?? 0;
        const priceTail = unit.lowPrice ? ` for ${asCurrency(unit.lowPrice)}` : '.';
        const priceTailPlural = unit.lowPrice ? ` starting at ${asCurrency(unit.lowPrice)}` : '.';
        return qty === 1
          ? `There is ${qty} ${bedString} available${priceTail}`
          : `There are ${qty} ${bedString}s available${priceTailPlural}`;
      }) ?? [];

  const questions: Array<FaqQuestion> = [];

  if (!isHouse && formattedUnits.length > 0) {
    questions.push({
      question: 'How many apartment units are available and what are their rents?',
      answerType: 'unorderedList',
      answer: formattedUnits,
    });
  }

  if (buildingName) {
    questions.push({
      question: `What is ${isHouse ? "this home's" : "the apartment building's"} pet policy?`,
      answerType: 'answer',
      answer: petPolicy
        ? `There are ${formattedPet} at ${buildingName}. Please contact the property manager for up to date pet policy and fee information.`
        : `Please contact the property manager at ${buildingName} for up to date pet policy information.`,
      // The visible answer keeps its "contact the property manager" tail; the
      // published one carries only the policy itself.
      ...(petPolicy ? { schemaAnswer: `There are ${formattedPet} at ${buildingName}.` } : {}),
    });

    questions.push({
      question: `Is there laundry in the ${isHouse ? 'house' : 'building'}?`,
      answerType: 'answer',
      answer:
        laundry && laundry !== 'none'
          ? `This building offers ${formattedLaundry} laundry.`
          : laundry === 'none'
            ? `No, there is no laundry offered at ${buildingName}.`
            : `Please contact ${buildingName} for information regarding laundry amenities.`,
      ...(laundry
        ? {
            schemaAnswer:
              laundry !== 'none'
                ? `This building offers ${formattedLaundry} laundry.`
                : `No, there is no laundry offered at ${buildingName}.`,
          }
        : {}),
    });

    questions.push({
      question: `Does the ${isHouse ? 'home' : 'building'} offer parking?`,
      answerType: 'answer',
      answer: parking
        ? `Yes, ${buildingName} provides parking.`
        : `For up to date tenant parking information, please contact ${buildingName}.`,
      ...(parking ? { schemaAnswer: `Yes, ${buildingName} provides parking.` } : {}),
    });

    if (city && neighborhood) {
      questions.push({
        question: `What ${city} neighborhood is ${buildingName} in?`,
        answerType: 'answer',
        answer: `Your new apartment would be located in the ${neighborhood} neighborhood.`,
      });
    }

    questions.push({
      question: `What school district is this ${isHouse ? 'home' : 'building'} in?`,
      answerType: 'answer',
      answer: schoolDistrict
        ? `${buildingName} is within the ${schoolDistrict}. Check out the additional Nearby Schools for more boundary details.`
        : `Please check with property manager to determine the school district ${buildingName} is in.`,
      ...(schoolDistrict ? { schemaAnswer: `${buildingName} is within the ${schoolDistrict}.` } : {}),
    });

    if (lastUpdatedAgo) {
      questions.push({
        question: 'When was this listing last updated?',
        answerType: 'answer',
        answer: `The property manager updated this rental listing ${lastUpdatedAgo}.`,
      });
    }
  }

  return questions;
};

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Answers that deflect to a human carry no information for an answer engine. */
const DEFLECTION =
  /please contact|contact the property manager|contact the landlord|check with property manager|please check with/i;

/**
 * A question is publishable when its answer is backed by data. `schemaAnswer`
 * overrides the visible answer where the two must differ; otherwise the visible
 * answer is published, unless it deflects to a human.
 */
const schemaQuestions = (questions: Array<FaqQuestion>): Array<FaqQuestion> =>
  questions
    .map((q) => (q.schemaAnswer === undefined ? q : { ...q, answer: q.schemaAnswer }))
    .filter((q) => {
      const answer = Array.isArray(q.answer) ? q.answer.join(' ') : q.answer;
      return Boolean(answer) && !DEFLECTION.test(answer);
    });

const formatAnswerForSchema = (q: FaqQuestion): string => {
  const type = q.answerType ?? 'answer';
  if (type === 'answer') return q.answer as string;

  const items = Array.isArray(q.answer) ? q.answer : [];
  const ListEl = type === 'unorderedList' ? 'ul' : 'ol';
  const lis = items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const list = `<${ListEl}>${lis}</${ListEl}>`;
  return q.listHeader ? `${q.listHeader}${list}` : list;
};

/**
 * The FAQ ships as its own script rather than inside the page graph: on the HDP
 * the module is backend-gated, and on the SRP it renders behind a Suspense
 * boundary that must not gate the shell. `pageUrl` links it back to that page's
 * node so the graph still connects.
 */
export const buildFaqJsonLd = (questions: Array<FaqQuestion>, pageUrl?: string) => {
  const publishable = schemaQuestions(questions);
  // An absent FAQPage is valid; one with an empty mainEntity is an error, the
  // same way an empty BreadcrumbList is (HP-9034).
  if (publishable.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(pageUrl ? { '@id': faqNodeId(pageUrl), isPartOf: { '@id': pageNodeId(pageUrl) } } : {}),
    mainEntity: publishable.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: formatAnswerForSchema(q),
      },
    })),
  };
};
